import type { TFunction } from 'i18next'
import type { PricingCampaignDataRow } from './usePricingCampaignsPickerTable'

import { useTranslation } from 'react-i18next'

import { getStatusBadgeVariant } from '@/entities/optimization'
import { EngineBadge, PricingEngineType } from '@/entities/pricing-campaign'

import { createSRTColumnHelper } from '@/shared/components/master-table'
import { Tag } from '@/shared/components/ui/tag'
import { formatNumber } from '@/shared/tools/locale'

const columnHelper = createSRTColumnHelper<PricingCampaignDataRow>()
export const getCamapignsPickerColumns = (
	{
		options,
	}: {
		options: Record<string, { label: string; value: string }[]>
	},
	t: TFunction
) => [
	columnHelper.accessor('name', {
		header: t('pricing_campaign'),
		meta: {
			label: t('pricing_campaign'),
			placeholder: t('pricing_campaign'),
			variant: 'text',
		},
		Cell: ({ cellValue }) => (
			<div className='truncate text-sm'>{cellValue}</div>
		),
		sortingFn: 'ogNameAndPcName',
	}),
	columnHelper.accessor('optimization_group_name', {
		id: 'optimization_group_name',
		size: 200,
		header: t('optimization_group'),
		meta: {
			label: t('optimization_group'),
			placeholder: t('optimization_group'),
			variant: 'multi-select',
			options: options.optimization_group_name || [],
		},
		Cell: ({ cellValue }) => (
			<div className='truncate text-sm'>{cellValue}</div>
		),
	}),
	columnHelper.accessor('products_count', {
		id: 'products_count',
		size: 80,
		header: t('products_count'),
		Cell: ({ cellValue }) => (
			<div className='truncate text-sm'>{formatNumber(cellValue || 0)}</div>
		),
	}),
	columnHelper.accessor('engine', {
		header: t('pricing_engine'),
		size: 200,
		meta: {
			label: t('pricing_engine'),
			placeholder: t('pricing_engine'),
			variant: 'multi-select',
			options: [
				{
					label: t(PricingEngineType.MD, { defaultValue: 'Markdown' }),
					value: PricingEngineType.MD,
				},
				{
					label: t(PricingEngineType.ML, { defaultValue: 'Demand-based' }),
					value: PricingEngineType.ML,
				},
				{
					label: t(PricingEngineType.RB, { defaultValue: 'Rule-based' }),
					value: PricingEngineType.RB,
				},
				{
					label: t(PricingEngineType.Promo, { defaultValue: 'Promo' }),
					value: PricingEngineType.Promo,
				},
			],
		},
		filterFn: (row, columnId, filterValue) =>
			filterValue.includes(row.original.engine),
		Cell: ({ cellValue }) => {
			const { t } = useTranslation()

			const engine = cellValue

			return (
				<EngineBadge engine={engine}>
					{t(engine, { defaultValue: engine })}
				</EngineBadge>
			)
		},
	}),
	columnHelper.accessor('optimization_status', {
		header: t('app.status'),
		size: 130,
		Cell: ({ cellValue }) => {
			const { t } = useTranslation()

			const value = cellValue

			return (
				<div className='truncate'>
					{!!value && (
						<Tag className='truncate' variant={getStatusBadgeVariant(value)}>
							{t(`opt_${value}`)}
						</Tag>
					)}
				</div>
			)
		},
	}),
]
